import React from 'react';
import { Box, Typography } from '@mui/material';
import IPhoneFrame, {
  StatusBar, NavBar, TabBar, HomeBar,
  IOS_FONT, IOS_BLUE, IOS_GRAY_BG, IOS_GRAY_FIELD, IOS_GRAY_TEXT, IOS_SEPARATOR,
} from './IPhoneFrame';
import {
  APP_LOGOS, APP_COLORS, TIMELINE_EVENTS, ACHIEVEMENTS as SHARED_ACHIEVEMENTS,
  IMPACT,
} from '../sharedMockData';

const TIMELINE = TIMELINE_EVENTS.map(e => ({
  day: e.day, when: e.when, app: e.app, title: e.title, points: e.points,
  color: APP_COLORS[e.appKey] || '#546e7a',
  logo: APP_LOGOS[e.appKey],
}));

const ACHIEVEMENTS = SHARED_ACHIEVEMENTS;

const IosProgressMockup = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, p: 3, bgcolor: '#eceff1', minHeight: '100%', flexWrap: 'wrap' }}>
    {/* Populated */}
    <IPhoneFrame
      title="4a. Progress — populated"
      caption="Impact-points header, recent activity timeline, achievements. Data is populated by Layer 5 backend Playwright scraping."
    >
      <StatusBar />
      <NavBar title="Progress" largeTitle subtitle={`+${IMPACT.weekDelta} impact points this week`} />
      <Box sx={{ position: 'absolute', top: 44 + 100, left: 0, right: 0, bottom: 83, overflow: 'auto' }}>
        <Box sx={{ px: 2, pt: 1 }}>
          {/* Impact points card */}
          <Box sx={{
            bgcolor: '#1a1a2e', color: '#fff',
            borderRadius: '18px', p: 2.5, mb: 2,
            display: 'flex', alignItems: 'center', gap: 2,
            boxShadow: '0 6px 16px rgba(26, 26, 46, 0.25)',
          }}>
            <Box sx={{
              width: 56, height: 56, borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '28px',
            }}>⚡</Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontFamily: IOS_FONT, fontSize: '12px', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Impact Points
              </Typography>
              <Typography sx={{ fontFamily: IOS_FONT, fontSize: '28px', fontWeight: 700 }}>
                {IMPACT.total.toLocaleString()}
              </Typography>
              <Typography sx={{ fontFamily: IOS_FONT, fontSize: '12px', color: '#58CC02' }}>
                ↑ {IMPACT.weekDelta} this week
              </Typography>
            </Box>
          </Box>

          {/* Achievements strip */}
          <Typography sx={{
            fontFamily: IOS_FONT, fontSize: '13px', color: IOS_GRAY_TEXT,
            textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, mb: 1,
          }}>Achievements</Typography>
          <Box sx={{ display: 'flex', gap: 1.2, overflowX: 'auto', mb: 2.5, pb: 0.5 }}>
            {ACHIEVEMENTS.map((a, i) => (
              <Box key={i} sx={{
                minWidth: 88, bgcolor: '#fff', borderRadius: 2,
                border: `0.5px solid ${IOS_SEPARATOR}`,
                p: 1.2, textAlign: 'center',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4,
              }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: '50%',
                  bgcolor: `${a.color}22`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '20px',
                }}>{a.emoji}</Box>
                <Typography sx={{ fontFamily: IOS_FONT, fontSize: '10px', fontWeight: 500, color: '#000', lineHeight: 1.1 }}>
                  {a.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Timeline */}
          <Typography sx={{
            fontFamily: IOS_FONT, fontSize: '13px', color: IOS_GRAY_TEXT,
            textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, mb: 1,
          }}>Recent Activity</Typography>
          <Box sx={{
            bgcolor: '#fff', borderRadius: '14px',
            border: `0.5px solid ${IOS_SEPARATOR}`, overflow: 'hidden',
          }}>
            {TIMELINE.map((t, i) => (
              <Box key={i}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.5 }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: 1.5,
                    bgcolor: '#fff',
                    border: `0.5px solid ${IOS_SEPARATOR}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden', flexShrink: 0,
                  }}>
                    {t.logo ? (
                      <Box component="img" src={t.logo} alt={t.app} sx={{ width: 30, height: 30, objectFit: 'contain' }} />
                    ) : (
                      <Box sx={{ width: '100%', height: '100%', bgcolor: t.color }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: IOS_FONT, fontSize: '13px', fontWeight: 600, color: '#000' }}>
                      {t.title}
                    </Typography>
                    <Typography sx={{ fontFamily: IOS_FONT, fontSize: '11px', color: IOS_GRAY_TEXT, mt: 0.3 }}>
                      {t.app} · {t.day} · {t.when}
                    </Typography>
                  </Box>
                  <Box sx={{
                    bgcolor: '#FFF4E5', color: '#E65100',
                    fontFamily: IOS_FONT, fontSize: '11px', fontWeight: 700,
                    px: 0.8, py: 0.3, borderRadius: 10,
                    flexShrink: 0, alignSelf: 'center',
                  }}>+{t.points}</Box>
                </Box>
                {i < TIMELINE.length - 1 && (
                  <Box sx={{ height: '0.5px', bgcolor: IOS_SEPARATOR, ml: 6.5 }} />
                )}
              </Box>
            ))}
          </Box>
          <Box sx={{ height: 20 }} />
        </Box>
      </Box>
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
        <TabBar activeIndex={1} />
      </Box>
      <HomeBar />
    </IPhoneFrame>

    {/* Empty state */}
    <IPhoneFrame
      title="4b. Progress — empty state"
      caption="First-launch / no-data empty state. Calls out that progress collection is scheduled server-side (Layer 5)."
    >
      <StatusBar />
      <NavBar title="Progress" largeTitle />
      <Box sx={{
        position: 'absolute', top: 44 + 100, left: 0, right: 0, bottom: 83,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        px: 4, textAlign: 'center',
      }}>
        <Box>
          <Box sx={{
            width: 96, height: 96, borderRadius: '50%',
            bgcolor: '#fff',
            border: `2px dashed ${IOS_SEPARATOR}`,
            mx: 'auto', mb: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '44px', color: IOS_GRAY_TEXT,
          }}>📊</Box>
          <Typography sx={{
            fontFamily: IOS_FONT, fontSize: '20px', fontWeight: 600, mb: 1,
          }}>No progress yet</Typography>
          <Typography sx={{
            fontFamily: IOS_FONT, fontSize: '14px', color: IOS_GRAY_TEXT, mb: 3,
          }}>
            Open an app from your dashboard and start learning.
            Progress will appear here after your next scheduled sync.
          </Typography>
          <Box sx={{
            bgcolor: IOS_BLUE,
            borderRadius: 2.5,
            py: 1.2, px: 3,
            color: '#fff',
            fontFamily: IOS_FONT, fontSize: '15px', fontWeight: 600,
            display: 'inline-block',
          }}>Browse Apps</Box>
          <Typography sx={{
            fontFamily: IOS_FONT, fontSize: '11px', color: IOS_GRAY_TEXT, mt: 3,
          }}>
            Next sync in 48 minutes
          </Typography>
        </Box>
      </Box>
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
        <TabBar activeIndex={1} />
      </Box>
      <HomeBar />
    </IPhoneFrame>
  </Box>
);

export default IosProgressMockup;
